/**
 * Non-Surgical Shoulder Treatment in Hyderabad
 * SEO-optimized page targeting "non surgical shoulder treatment", "shoulder pain treatment without surgery"
 * Comprehensive guide to conservative treatment options
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { CTASection } from '../components/CTASection';
import { FAQSection } from '../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Syringe, 
  Activity, 
  Zap, 
  Pill, 
  Heart, 
  ArrowRight, 
  CheckCircle, 
  Clock,
  IndianRupee,
  Stethoscope,
  Shield,
  Target,
  Phone
} from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export const NonSurgicalShoulderTreatment = () => {
  const { theme, doctor } = useSite();

  // Non-surgical treatment options
  const treatments = [
    {
      icon: <Activity className="w-8 h-8" />,
      title: "Physiotherapy",
      subtitle: "First-Line Treatment",
      description: "Structured exercise programs to restore movement, strength, and function. The foundation of non-surgical shoulder care.",
      details: [
        "Range of motion exercises",
        "Rotator cuff strengthening",
        "Scapular stabilization",
        "Posture correction",
        "Manual therapy techniques"
      ],
      effectiveness: "60-80% improvement for most conditions",
      duration: "6-12 weeks typical program",
      cost: "₹500-1,000 per session",
      bestFor: ["Rotator cuff tears (partial)", "Frozen shoulder", "Impingement", "Post-injury rehabilitation"]
    },
    {
      icon: <Syringe className="w-8 h-8" />,
      title: "Corticosteroid Injections",
      subtitle: "Anti-Inflammatory",
      description: "Powerful anti-inflammatory medication injected directly into the shoulder joint or subacromial space for rapid pain relief.",
      details: [
        "Ultrasound-guided for accuracy",
        "Rapid pain relief (24-72 hours)",
        "Reduces inflammation",
        "Enables better physiotherapy",
        "Can be repeated (limited times)"
      ],
      effectiveness: "70-90% pain relief initially",
      duration: "Relief lasts 4-12 weeks",
      cost: "₹2,000-4,000 per injection",
      bestFor: ["Frozen shoulder", "Impingement", "Bursitis", "Arthritis flares", "Calcific tendonitis (acute)"]
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "PRP Therapy (Platelet-Rich Plasma)",
      subtitle: "Regenerative Medicine",
      description: "Your own blood is processed to concentrate healing platelets, then injected to promote tissue repair and regeneration.",
      details: [
        "Uses your own blood (autologous)",
        "Concentrates growth factors",
        "Promotes natural healing",
        "Ultrasound-guided injection",
        "Minimal side effects"
      ],
      effectiveness: "50-70% improvement reported",
      duration: "Results develop over 4-8 weeks",
      cost: "₹8,000-15,000 per session",
      bestFor: ["Partial rotator cuff tears", "Tendinopathy", "Early arthritis", "Chronic tendon problems"]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Shockwave Therapy (ESWT)",
      subtitle: "Non-Invasive",
      description: "High-energy acoustic waves stimulate healing in damaged tendons. Particularly effective for calcific tendonitis.",
      details: [
        "No needles or injections",
        "Outpatient procedure",
        "Breaks down calcifications",
        "Stimulates blood flow",
        "Promotes tissue repair"
      ],
      effectiveness: "70-80% improvement",
      duration: "4-6 sessions, 1 week apart",
      cost: "₹3,000-5,000 per session",
      bestFor: ["Calcific tendonitis", "Chronic tendinopathy", "Plantar fasciitis", "Tennis elbow"]
    },
    {
      icon: <Syringe className="w-8 h-8" />,
      title: "Hydrodilatation",
      subtitle: "For Frozen Shoulder",
      description: "Large volume saline injection to stretch the contracted shoulder capsule. Specifically designed for frozen shoulder.",
      details: [
        "Stretches tight capsule",
        "Done under ultrasound guidance",
        "Combined with steroid",
        "Immediate improvement in motion",
        "Followed by intensive physio"
      ],
      effectiveness: "60-75% improvement in motion",
      duration: "Single procedure + physio",
      cost: "₹8,000-12,000",
      bestFor: ["Frozen shoulder (adhesive capsulitis)", "Post-surgical stiffness"]
    },
    {
      icon: <Syringe className="w-8 h-8" />,
      title: "Hyaluronic Acid Injections",
      subtitle: "Viscosupplementation",
      description: "Gel-like substance injected into the joint to improve lubrication and cushioning. Like oil for a squeaky joint.",
      details: [
        "Improves joint lubrication",
        "Cushions the cartilage",
        "Reduces friction",
        "May slow arthritis progression",
        "Series of 3-5 injections"
      ],
      effectiveness: "50-70% for mild-moderate arthritis",
      duration: "Relief may last 6-12 months",
      cost: "₹5,000-10,000 per injection",
      bestFor: ["Early shoulder arthritis", "Cartilage wear", "Patients wanting to delay replacement"]
    }
  ];

  // Conditions that respond well to non-surgical treatment
  const responsiveConditions = [
    {
      condition: "Frozen Shoulder",
      success: "90%",
      note: "Most cases resolve without surgery",
      link: "/frozen-shoulder"
    },
    {
      condition: "Shoulder Impingement",
      success: "80%",
      note: "Physio + injection often sufficient",
      link: "/shoulder-impingement"
    },
    {
      condition: "Partial Rotator Cuff Tear",
      success: "70%",
      note: "Small tears often heal with conservative care",
      link: "/rotator-cuff-tear"
    },
    {
      condition: "Calcific Tendonitis",
      success: "70%",
      note: "Many resolve naturally or with barbotage",
      link: "/calcific-tendonitis"
    },
    {
      condition: "Early Shoulder Arthritis",
      success: "60%",
      note: "Can delay surgery for years",
      link: "/shoulder-arthritis"
    },
    {
      condition: "Mild Instability / MDI",
      success: "80%",
      note: "Rehabilitation-first approach",
      link: "/mdi"
    }
  ];

  // FAQs
  const faqs = [
    {
      question: "Can shoulder problems be treated without surgery?",
      answer: "Yes, the majority of shoulder conditions can be effectively managed without surgery. Studies show that 60-80% of patients with rotator cuff problems, impingement, and frozen shoulder improve with non-surgical treatment. Dr. Deepthi Nandan Reddy always explores conservative options first, recommending surgery only when non-surgical treatment fails or for specific conditions that require it."
    },
    {
      question: "How long does non-surgical treatment take to work?",
      answer: "It depends on the treatment: Corticosteroid injections provide relief within 24-72 hours but may take 2 weeks for full effect. Physiotherapy typically shows improvement within 4-6 weeks, with full programs lasting 12 weeks. PRP therapy takes 4-8 weeks to show results. Shockwave therapy requires 4-6 sessions over 4-6 weeks. Frozen shoulder may take 12-18 months to fully resolve."
    },
    {
      question: "Are injections painful?",
      answer: "Most patients describe the discomfort as minimal – similar to a blood test. Dr. Reddy uses ultrasound guidance for accurate needle placement, which improves effectiveness and reduces discomfort. Local anesthetic is often mixed with the injection. Some soreness for 24-48 hours after is common, especially with PRP."
    },
    {
      question: "How much does non-surgical shoulder treatment cost in Hyderabad?",
      answer: "At Apollo Hospitals Financial District: Physiotherapy ₹500-1,000 per session, Corticosteroid injection ₹2,000-4,000, PRP therapy ₹8,000-15,000, Shockwave therapy ₹3,000-5,000 per session (4-6 needed), Hydrodilatation ₹8,000-12,000, Hyaluronic acid ₹5,000-10,000 per injection. Many insurance policies cover these treatments."
    },
    {
      question: "When is surgery necessary instead of non-surgical treatment?",
      answer: "Surgery may be recommended for: Complete rotator cuff tears (especially in younger/active patients), recurrent shoulder dislocations, failed conservative treatment after 6+ months, severe arthritis affecting quality of life, traumatic injuries (fractures, acute large tears), or when non-surgical treatment provides only temporary relief."
    },
    {
      question: "Can I do physiotherapy exercises at home?",
      answer: "Yes, home exercises are a crucial part of recovery. However, initial sessions should be supervised by a physiotherapist to ensure correct technique and prevent further injury. Dr. Reddy's team provides personalized exercise programs with video guidance. Consistency is key – exercises should be done daily for best results."
    }
  ];

  return (
    <div className="min-h-screen" data-testid="non-surgical-treatment-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 bg-green-100 text-green-800">
              <Shield className="w-4 h-4" />
              Conservative Care First
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Non-Surgical Shoulder Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Physiotherapy, injections, PRP, shockwave therapy & more. 70% of shoulder conditions 
              improve without surgery. Expert care by Dr. Deepthi Nandan Reddy at Apollo Hospitals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={doctor.bookingLink} target="_blank" rel="noopener noreferrer">
                <button 
                  className="px-8 py-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: theme?.primaryHex }}
                >
                  <Phone className="w-5 h-5" />
                  Book Consultation
                </button>
              </a>
              <a href={`tel:${doctor.phone}`}>
                <button 
                  className="px-8 py-4 rounded-lg border-2 font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                  style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}
                >
                  Call {doctor.phone}
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Approach: Surgery as Last Resort</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                At Apollo Hospitals Financial District, Dr. Deepthi Nandan Reddy believes in exhausting all non-surgical 
                options before recommending surgery. With over 30 years of experience, he has seen that the majority of 
                shoulder conditions – including many rotator cuff tears – can be successfully managed without an operation.
              </p>
              <div className="bg-green-50 border-l-4 border-green-500 p-6 my-6">
                <p className="font-semibold text-gray-900 mb-2">Did You Know?</p>
                <p className="text-gray-700">
                  Studies show that <strong>60-80% of patients</strong> with rotator cuff tears can function well 
                  without surgery when treated with appropriate physiotherapy and injections. Even some full-thickness 
                  tears can be managed conservatively in older, less active patients.
                </p>
              </div>
              <p>
                Non-surgical treatment is not just about avoiding the operating room – it's often the most 
                effective approach. Conditions like frozen shoulder have a natural recovery course, and surgery 
                rarely speeds this up. Impingement syndrome responds excellently to targeted exercises. The key 
                is accurate diagnosis and the right treatment for your specific condition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Options */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Non-Surgical Treatment Options</h2>
              <p className="text-gray-600">Comprehensive conservative care available at Apollo Hospitals, Hyderabad</p>
            </div>

            <div className="space-y-8">
              {treatments.map((treatment, index) => (
                <Card key={index} className="overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-1/3 p-6 bg-gradient-to-br from-gray-50 to-white">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 rounded-lg" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>
                          {treatment.icon}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{treatment.title}</h3>
                          <span className="text-sm text-gray-500">{treatment.subtitle}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">{treatment.description}</p>
                    </div>
                    <div className="md:w-2/3 p-6 border-t md:border-t-0 md:border-l border-gray-100">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <p className="font-semibold text-gray-900 mb-2 text-sm">What's Involved:</p>
                          <ul className="space-y-1 text-sm text-gray-600">
                            {treatment.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: theme?.primaryHex }} />
                                {detail}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4" style={{ color: theme?.primaryHex }} />
                            <span className="text-sm"><strong>Effectiveness:</strong> {treatment.effectiveness}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" style={{ color: theme?.primaryHex }} />
                            <span className="text-sm"><strong>Duration:</strong> {treatment.duration}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <IndianRupee className="w-4 h-4" style={{ color: theme?.primaryHex }} />
                            <span className="text-sm"><strong>Cost:</strong> {treatment.cost}</span>
                          </div>
                          <div className="pt-2">
                            <p className="text-xs font-semibold text-gray-700 mb-1">Best For:</p>
                            <div className="flex flex-wrap gap-1">
                              {treatment.bestFor.map((item, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Rates by Condition */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Conditions That Respond Well to Non-Surgical Treatment</h2>
              <p className="text-gray-600">Click to learn more about each condition and treatment options</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {responsiveConditions.map((item, index) => (
                <Link key={index} to={item.link}>
                  <Card className="h-full hover:shadow-lg transition-all cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-gray-900">{item.condition}</h3>
                        <span 
                          className="text-2xl font-bold"
                          style={{ color: theme?.primaryHex }}
                        >
                          {item.success}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{item.note}</p>
                      <span className="text-sm font-medium flex items-center gap-1" style={{ color: theme?.primaryHex }}>
                        Learn more <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <div className="mt-8 p-6 bg-orange-50 rounded-lg">
              <p className="text-gray-800">
                <strong>Note:</strong> Success rates are general estimates. Your individual outcome depends on factors 
                like condition severity, duration of symptoms, age, activity level, and compliance with treatment. 
                Dr. Reddy will provide a personalized prognosis based on your specific situation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Summary */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <IndianRupee className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Cost Summary – Non-Surgical Treatments</h2>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <p className="text-gray-600 mb-4">Indicative costs at Apollo Hospitals Financial District, Hyderabad:</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Initial Consultation</span>
                  <span className="font-semibold">₹800-1,500</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Physiotherapy (per session)</span>
                  <span className="font-semibold">₹500-1,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Corticosteroid Injection</span>
                  <span className="font-semibold">₹2,000-4,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">PRP Therapy</span>
                  <span className="font-semibold">₹8,000-15,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Shockwave Therapy (per session)</span>
                  <span className="font-semibold">₹3,000-5,000</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-700">Hydrodilatation</span>
                  <span className="font-semibold">₹8,000-12,000</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-700">Hyaluronic Acid Injection</span>
                  <span className="font-semibold">₹5,000-10,000</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Typical treatment course: Consultation + Imaging (₹3,000-5,000) + Physiotherapy program (₹6,000-12,000) 
                ± Injection (₹2,000-15,000). Total: ₹10,000-30,000 for most non-surgical treatment plans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-center bg-gradient-to-r from-green-50 to-white p-8 rounded-xl border border-green-100">
              <img 
                src={doctor.doctorImage} 
                alt={doctor.name}
                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
              />
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Your Non-Surgical Treatment</h2>
                <p className="text-gray-600 mb-4">
                  Get an accurate diagnosis and personalized treatment plan. Dr. Deepthi Nandan Reddy will 
                  recommend the most appropriate non-surgical options for your condition – surgery is only 
                  considered when conservative treatment fails.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <a href={doctor.bookingLink} target="_blank" rel="noopener noreferrer">
                    <button 
                      className="px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: theme?.primaryHex }}
                    >
                      Book at Apollo Hospitals
                    </button>
                  </a>
                  <a href={`tel:${doctor.phone}`}>
                    <button 
                      className="px-6 py-3 rounded-lg border-2 font-medium hover:bg-white transition-colors"
                      style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}
                    >
                      Call {doctor.phone}
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Related Resources</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/shoulder-pain-treatment-hyderabad" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>All Shoulder Conditions</Link>
              <Link to="/rotator-cuff-tear" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Rotator Cuff Tear</Link>
              <Link to="/frozen-shoulder" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Frozen Shoulder</Link>
              <Link to="/shoulder-impingement" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Shoulder Impingement</Link>
              <Link to="/calcific-tendonitis" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Calcific Tendonitis</Link>
              <Link to="/shoulder-arthritis" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Shoulder Arthritis</Link>
              <Link to="/arthroscopic-shoulder-surgery" className="px-4 py-2 rounded-full text-sm border hover:bg-gray-50 transition-opacity" style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}>When Surgery is Needed →</Link>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default NonSurgicalShoulderTreatment;
